import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { subscriptionsService } from '@/services/subscriptions';
import { getApiError } from '@/services/api';
import type { SubscriptionPlan, SubscriptionPayment } from '@dpfs/shared';
import clsx from 'clsx';
import { CreditCard, Check, XCircle, Loader2, X } from 'lucide-react';
import { PageHeader, LoadingState, EmptyState } from '@/components/ui';

type BillingInterval = 'MONTHLY' | 'YEARLY';

export function SubscriptionPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [interval, setInterval] = useState<BillingInterval>('MONTHLY');
  const [error, setError] = useState<string | null>(null);

  const paymentStatus = searchParams.get('payment');
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: () => subscriptionsService.getPlans(),
  });

  const { data: subData, isLoading: subLoading } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionsService.getCurrentSubscription(),
  });

  const checkoutMutation = useMutation({
    mutationFn: ({ planId, billingInterval }: { planId: string; billingInterval: BillingInterval }) =>
      subscriptionsService.createCheckout(planId, billingInterval),
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => subscriptionsService.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const subscription = subData?.subscription;
  const plans = plansData?.plans || [];

  const formatPrice = (centimes: number) => {
    return new Intl.NumberFormat(dateLocale, { style: 'decimal' }).format(centimes / 100);
  };

  const getPlanName = (plan: SubscriptionPlan) => {
    return i18n.language === 'ar' && plan.nameAr ? plan.nameAr : plan.nameFr;
  };

  const getPlanDescription = (plan: SubscriptionPlan) => {
    return i18n.language === 'ar' && plan.descriptionAr ? plan.descriptionAr : plan.descriptionFr;
  };

  const getYearlyDiscount = (plan: SubscriptionPlan) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    if (monthlyTotal === 0) return 0;
    return Math.round((1 - plan.yearlyPrice / monthlyTotal) * 100);
  };

  if (plansLoading || subLoading) {
    return <LoadingState />;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title={t('subscription.title')} description={t('pharmacist.subscription.description')} />

      {/* Error message */}
      {error && (
        <div className="alert-danger mb-6 items-center" role="alert">
          <XCircle className="h-5 w-5 shrink-0" />
          <p className="min-w-0 flex-1 break-words">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="-m-1 rounded p-1 hover:bg-danger-100"
            aria-label={t('pharmacist.common.dismiss')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Payment status messages */}
      {paymentStatus === 'success' && (
        <div className="alert-success mb-6 items-center">
          <Check className="h-5 w-5 shrink-0" />
          <p>{t('subscription.paymentSuccess')}</p>
        </div>
      )}
      {paymentStatus === 'failed' && (
        <div className="alert-danger mb-6 items-center">
          <XCircle className="h-5 w-5 shrink-0" />
          <p>{t('subscription.paymentFailed')}</p>
        </div>
      )}

      {/* Current subscription status */}
      {subscription && (
        <div className="card mb-8 p-5 sm:p-6">
          <h2 className="section-title mb-3">{t('subscription.currentPlan')}</h2>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-lg font-semibold text-gray-900">{getPlanName(subscription.plan)}</p>
              <p className="text-sm text-gray-500">
                {subscription.billingInterval === 'MONTHLY' ? t('subscription.monthly') : t('subscription.yearly')}
              </p>
            </div>
            <StatusBadge status={subscription.status} />
          </div>

          {subscription.status === 'CANCELLED' && subscription.cancelledAt && (
            <p className="mb-2 text-sm text-warning-700">
              {t('subscription.cancelledUntil', {
                date: new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale),
              })}
            </p>
          )}

          {subscription.status !== 'EXPIRED' && subscription.currentPeriodEnd && (
            <p className="text-sm text-gray-500">
              {t('subscription.expiresOn')}: {new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale)}
            </p>
          )}

          {subscription.status === 'ACTIVE' && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('subscription.cancelConfirm'))) {
                  cancelMutation.mutate();
                }
              }}
              disabled={cancelMutation.isPending}
              className="btn-ghost btn-sm -ms-3 mt-3 text-danger-600 hover:bg-danger-50 hover:text-danger-700"
            >
              {cancelMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('subscription.cancel')}
            </button>
          )}

          {/* Payment history */}
          {subscription.payments && subscription.payments.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="section-title mb-3">{t('subscription.history')}</h3>
              <ul className="divide-y divide-gray-100">
                {subscription.payments.map((payment: SubscriptionPayment) => (
                  <li key={payment.id} className="grid grid-cols-3 items-center gap-3 py-2 text-sm">
                    <span className="text-gray-600">
                      {new Date(payment.createdAt).toLocaleDateString(dateLocale)}
                    </span>
                    <span className="text-center font-medium tabular-nums text-gray-900">
                      {formatPrice(payment.amount)} DZD
                    </span>
                    <span className="text-end">
                      <PaymentStatusBadge status={payment.status} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Plan selection */}
      {(!subscription || subscription.status === 'EXPIRED' || subscription.status === 'PAST_DUE') && (
        <>
          {/* Billing interval toggle */}
          <div className="flex justify-center mb-6">
            <div className="flex rounded-lg bg-gray-100 p-1" role="group">
              <button
                type="button"
                aria-pressed={interval === 'MONTHLY'}
                onClick={() => setInterval('MONTHLY')}
                className={clsx(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  interval === 'MONTHLY'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {t('subscription.monthly')}
              </button>
              <button
                type="button"
                aria-pressed={interval === 'YEARLY'}
                onClick={() => setInterval('YEARLY')}
                className={clsx(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  interval === 'YEARLY'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {t('subscription.yearly')}
              </button>
            </div>
          </div>

          {plans.map((plan) => {
            const price = interval === 'MONTHLY' ? plan.monthlyPrice : plan.yearlyPrice;
            const discount = getYearlyDiscount(plan);
            const description = getPlanDescription(plan);

            return (
              <div key={plan.id} className="card mb-4 p-5 sm:p-6">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">{getPlanName(plan)}</h3>
                    {description && (
                      <p className="text-sm text-gray-500 mt-1">{description}</p>
                    )}
                  </div>
                  <div className="shrink-0 sm:text-end">
                    <p className="text-2xl font-bold tabular-nums text-gray-900">
                      {formatPrice(price)} <span className="text-sm font-normal text-gray-500">DZD</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {interval === 'MONTHLY' ? t('subscription.perMonth') : t('subscription.perYear')}
                    </p>
                    {interval === 'YEARLY' && discount > 0 && (
                      <p className="mt-1 text-xs font-medium text-success-700">
                        {t('subscription.yearlyDiscount', { percent: discount })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Payment methods */}
                <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
                  <CreditCard className="w-4 h-4" />
                  <span>{t('subscription.paymentMethods')}: EDAHABIA, CIB</span>
                </div>

                <button
                  type="button"
                  onClick={() => checkoutMutation.mutate({ planId: plan.id, billingInterval: interval })}
                  disabled={checkoutMutation.isPending}
                  className="btn-primary btn-lg w-full py-3"
                >
                  {checkoutMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('subscription.processing')}
                    </>
                  ) : subscription ? (
                    t('subscription.renew')
                  ) : (
                    t('subscription.subscribe')
                  )}
                </button>
              </div>
            );
          })}
        </>
      )}

      {!subscription && plans.length === 0 && (
        <div className="card p-0">
          <EmptyState icon={CreditCard} title={t('subscription.noSubscription')} />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const config: Record<string, { className: string; label: string }> = {
    ACTIVE: { className: 'badge-success', label: t('subscription.active') },
    PAST_DUE: { className: 'badge-warning', label: t('subscription.statusPastDue') },
    EXPIRED: { className: 'badge-danger', label: t('subscription.statusExpired') },
    CANCELLED: { className: 'badge-gray', label: t('subscription.cancelled') },
  };
  const c = config[status] || config['EXPIRED']!;
  return <span className={clsx(c.className, 'shrink-0')}>{c.label}</span>;
}

function PaymentStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    paid: t('subscription.paid'),
    pending: t('subscription.pending'),
    failed: t('subscription.failed'),
    canceled: t('subscription.cancelled'),
  };
  const colors: Record<string, string> = {
    paid: 'badge-success',
    pending: 'badge-warning',
    failed: 'badge-danger',
    canceled: 'badge-gray',
  };
  return (
    <span className={colors[status] || 'badge-gray'}>
      {labels[status] || status}
    </span>
  );
}
